import React, { useState, useEffect } from 'react';
import { builder } from '@builder.io/react';
import { useRouter } from 'next/router';

import Button from 'react-scale/components/elements/button';

import Header from 'react-scale/components/elements/header';
import SimpleHero from 'react-scale/components/sections/hero/simple-centered';
import BlogItem from 'react-scale/components/pages/blog/index/BlogItem';
import Paginator from 'react-scale/components/elements/paginator';
import Footer from 'react-scale/components/elements/footer';
import Layout from 'react-scale/components/pages/blog/layout';
import handleError from 'react-scale/lib/handle-error';

import { Wrapper } from 'react-aria-tabpanel';

const BUILDER_API_KEY = process.env.NEXT_PUBLIC_BUILDER_API_KEY;
builder.init(BUILDER_API_KEY);

const PAGE_SIZE = 9;

const BlogList = ({ children }) => {
  return children({
    blogs: []
  });
};

export default function BlogIndex({ blogs, preview }) {
  const router = useRouter();
  const { page: pageQuery } = router.query;
  const [mobileItems, setMobileItems] = useState(PAGE_SIZE);

  const blogsItems = blogs.map(post => (
    <>{!post.noIndex && <BlogItem post={post} key={post.slug} preview={preview} />}</>
  ));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setMobileItems(router.query?.page * PAGE_SIZE || PAGE_SIZE);
    }
  }, [router.query.page]);

  return (
    <Layout preview={preview}>
      <div className="bg-neutral-100">
        <Header
          title="AI & ML Blog | Scale AI"
          canonical="https://scale.com/blog"
          border
          theme="light"
        />
        <SimpleHero title="Blog" subtitle="Company Updates & Technology Articles" theme="light" />
        <BlogList>
          {() => {
            const scrollToPageStart = () => {
              document.getElementById('page').scrollIntoView({
                behavior: 'smooth',
                top: -10
              });
            };

            const allItemsSorted = blogsItems.sort((a, b) => {
              a = a.props.date || '';
              b = b.props.date || '';

              return b.localeCompare(a);
            });

            const onClickSeeMore = () => {
              if (allItemsSorted.length > mobileItems) {
                setMobileItems(mobileItems + PAGE_SIZE);
              }
            };

            const pageNumber = pageQuery ? parseInt(pageQuery.toString(), 10) - 1 : 0;
            const pagesAmount = Math.ceil(allItemsSorted.length / PAGE_SIZE);

            return (
              <div id="page" className="container w-full pb-20">
                <Wrapper className="space-y-[72px] md:space-y-8">
                  <div className="hidden md:block">
                    <div>
                      {allItemsSorted
                        .slice(pageNumber * PAGE_SIZE, pageNumber * PAGE_SIZE + PAGE_SIZE)
                        .map(box => box)}
                    </div>
                  </div>
                  <div className="block mb-16 md:hidden">
                    <div>{allItemsSorted.slice(0, mobileItems).map(box => box)}</div>
                  </div>

                  <div className="hidden md:block">
                    <Paginator
                      pages={pagesAmount}
                      currentPage={pageNumber}
                      isInPage
                      pageCallback={scrollToPageStart}
                    />
                  </div>

                  {mobileItems < allItemsSorted.length && (
                    <div className="block mx-auto md:hidden max-w-max">
                      <Button
                        onClick={onClickSeeMore}
                        text="See More"
                        hideArrow
                        type="primary"
                        variant="black"
                      />
                    </div>
                  )}
                </Wrapper>
              </div>
            );
          }}
        </BlogList>
        <Footer border theme="light" />
      </div>
    </Layout>
  );
}

BlogIndex.theme = 'light';

export async function getStaticProps() {
  const props = {};

  const builderBlogs = await builder.getAll('blog-model', {
    options: { noTargeting: true },
    omit: 'data.blocks',
    limit: 200
  });

  try {
    props.blogs = builderBlogs?.map(blog => blog.data);
  } catch (error) {
    handleError(error);
  }

  return { props, revalidate: 1 };
}
